import { useState } from "react";
import { format, parseISO } from "date-fns";
import { nb } from "date-fns/locale";
import { formatHours, addMinutesToTime } from "@/lib/format";
import { SrIcon } from "@/components/ui/SrIcon";
import { RegistrationEditDialog } from "./RegistrationEditDialog";
import type { TimeEntry } from "@/hooks/use-time-registration";

/** Check if a date string has a real time component (not midnight) */
function hasTimeComponent(dateStr: string): boolean {
  const d = new Date(dateStr);
  return d.getHours() !== 0 || d.getMinutes() !== 0 || d.getSeconds() !== 0;
}

/** Extract HH:MM from an ISO date string */
function extractTime(dateStr: string): string {
  const d = new Date(dateStr);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/**
 * VIB-1220: «Registrerte timer»-listen i TimeTrackingPopup, utvidet med
 * rediger-knapp (blyant) ved siden av slett. Egen komponent fordi
 * TimeTrackingPopup allerede er over 300-linjersgrensen (jf. CLAUDE.md
 * filstørrelsesregel + endringssjekk-notatet på issuen).
 *
 * Redigering går via samme `RegistrationEditDialog` som Timer-kortet og
 * DayGridView bruker (VIB-704) — én redigeringsflyt i hele appen.
 */
export default function TimeTrackingEntryLog({
  entries,
  onDelete,
  onEditDialogOpenChange,
}: {
  entries: TimeEntry[];
  onDelete: (entryId: string) => void;
  /**
   * VIB-1220-regresjon (A3, smoke-test 2026-07-18): TimeTrackingPopup lukker
   * seg selv ved klikk utenfor `popupRef` (desktop). RegistrationEditDialog
   * portales til `document.body` — UTENFOR popupRef — så ethvert klikk inni
   * den (f.eks. for å endre klokkeslett) ble stille tolket som "klikk
   * utenfor" og lukket hele den ytre popupen. Varsler forelderen når denne
   * dialogen åpner/lukker, slik at forelderen kan pause sin egen
   * outside-click-lukking mens den er åpen.
   */
  onEditDialogOpenChange?: (open: boolean) => void;
}) {
  const [editingEntry, setEditingEntryState] = useState<TimeEntry | null>(null);

  const setEditingEntry = (entry: TimeEntry | null) => {
    setEditingEntryState(entry);
    onEditDialogOpenChange?.(entry !== null);
  };

  if (entries.length === 0) return null;

  return (
    <div className="px-4 py-2 border-t">
      <p className="text-xs text-muted-foreground font-medium mb-1.5">Registrerte timer</p>
      {entries.map((entry) => (
        <div key={entry.id} className="flex items-center gap-2 py-2 text-xs border-b border-border/30 last:border-0">
          <span className="text-muted-foreground flex-shrink-0">
            {format(parseISO(entry.start_date), "dd. MMM", { locale: nb })}
          </span>
          {hasTimeComponent(entry.start_date) && (
            <span className="text-muted-foreground tabular-nums flex-shrink-0">
              {extractTime(entry.start_date)}–{addMinutesToTime(extractTime(entry.start_date), Math.round(entry.duration_seconds / 60))}
            </span>
          )}
          <span className="font-medium tabular-nums flex-shrink-0">
            {formatHours(entry.duration_seconds / 3600)}
          </span>
          <div className="ml-auto flex items-center gap-0.5">
            <button
              onClick={() => setEditingEntry(entry)}
              className="w-8 h-8 xl:w-7 xl:h-7 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Rediger"
            >
              <SrIcon name="pencil" className="w-4 h-4" />
            </button>
            <button
              onClick={() => onDelete(entry.id)}
              className="w-8 h-8 xl:w-7 xl:h-7 flex items-center justify-center text-muted-foreground hover:text-destructive transition-colors"
              aria-label="Slett"
            >
              <SrIcon name="trash" className="w-4 h-4" />
            </button>
          </div>
        </div>
      ))}

      <RegistrationEditDialog
        entry={editingEntry}
        open={!!editingEntry}
        onOpenChange={(o) => !o && setEditingEntry(null)}
      />
    </div>
  );
}
